<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="2"></v-col>
      <v-col cols="12" sm="12" md="8">
        <v-card class="mx-auto my-5 mb-10" max-width="1000">
          <v-app-bar flat>
            <span class="headline">{{ formTitle }}</span>
            <v-chip small outlined color="primary" class="ml-3">
              {{
                isAdvanced
                  ? $t("riskAssessment.kind.advancedShort")
                  : $t("riskAssessment.kind.basicShort")
              }}
            </v-chip>
          </v-app-bar>
          <v-card-text>
            <v-container>
              <v-form ref="form">
                <v-row dense>
                  <v-col cols="12" :md="isOwner ? 8 : 12" class="py-1">
                    <v-text-field
                      v-model="formData.name"
                      :label="$t('riskAssessment.templates.name')"
                      :rules="[
                        (v) => !!v || $t('general.fieldNeedsToBeFilled'),
                      ]"
                      data-cy="templateName"
                    />
                  </v-col>
                  <v-col cols="12" md="4" v-if="isOwner" class="py-1">
                    <v-select
                      v-model="formData.type"
                      :items="typeOptions"
                      :label="$t('riskAssessment.templates.type')"
                      item-text="text"
                      item-value="value"
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="py-1">
                    <v-select
                      v-model="formData.category"
                      :items="folderOptions"
                      :label="$t('riskAssessment.templates.folder.label')"
                      item-text="text"
                      item-value="value"
                      clearable
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <riskAssessmentRisks
                      v-model="formData"
                      :config="{ advanced: isAdvanced, template: true }"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="grey" outlined @click="goBack">{{
              $t("general.back")
            }}</v-btn>
            <v-btn color="primary" @click="saveTemplate" data-cy="saveTemplate">
              {{ isEditMode ? $t("general.saveChanges") : $t("general.save") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="2"></v-col>
    </v-row>
  </div>
</template>

<script>
import { isMaster } from "../../utils/masterEmails";
import riskAssessmentRisks from "../../components/formSlotComponents/riskAssessment/riskAssessmentRisks.vue";

export default {
  name: "riskAssessmentTemplateForm",
  components: { riskAssessmentRisks },
  data() {
    return {
      formData: {
        name: "",
        type: "local",
        kind: this.$route.query.kind === "advanced" ? "advanced" : "basic",
        category: null,
        risks: [],
      },
      isOwner: false,
      folders: [],
      typeOptions: [
        { text: "Global", value: "global" },
        { text: "Local", value: "local" },
      ],
    };
  },
  computed: {
    isEditMode() {
      return this.$route.params.mode === "edit";
    },
    isAdvanced() {
      return this.formData.kind === "advanced";
    },
    formTitle() {
      return this.isEditMode
        ? this.$t("riskAssessment.templates.editTemplate")
        : this.$t("riskAssessment.templates.addTemplate");
    },
    folderOptions() {
      const none = {
        text: this.$t("riskAssessment.templates.folder.ohneOrdner"),
        value: null,
      };
      const matching = this.folders
        .filter((f) => f.type === this.formData.type)
        .map((f) => ({ text: f.name, value: f._id }));
      return [none, ...matching];
    },
  },
  watch: {
    "formData.type"(newType) {
      if (!this.formData.category) return;
      const folder = this.folders.find((f) => f._id === this.formData.category);
      if (!folder || folder.type !== newType) {
        this.formData.category = null;
      }
    },
  },
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.isOwner = user && isMaster(user.email);

    const foldersRes = await this.$store.dispatch(
      "getRiskAssessmentTemplateCategories",
    );
    this.folders = (foldersRes && foldersRes.data) || [];

    if (this.isEditMode) {
      const item = await this.$store.dispatch(
        "getSingleRiskAssessmentTemplate",
        this.$route.params.id,
      );
      if (item) {
        this.formData = {
          _id: item._id,
          name: item.name,
          type: item.type,
          kind: item.kind === "advanced" ? "advanced" : "basic",
          category: (item.category && (item.category._id || item.category)) || null,
          risks: (item.risks || []).map((risk) => ({
            ...risk,
            dialogs: {
              classificationDialog: { open: false },
              afterActionDialog: { open: false },
            },
          })),
        };
      }
    } else {
      const preCat = this.$route.params.templateCategoryId;
      if (preCat && preCat !== "none") {
        const folder = this.folders.find((f) => f._id === preCat);
        if (folder) {
          this.formData.category = folder._id;
          this.formData.type = folder.type;
        }
      }
    }
  },
  methods: {
    async saveTemplate() {
      if (!this.$refs.form.validate()) return;
      if (this.formData.risks.length === 0) {
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("riskAssessment.templates.atLeastOneRisk"),
        );
        return;
      }

      if (!this.isOwner) {
        this.formData.type = "local";
      }

      const payload = {
        ...this.formData,
        risks: this.formData.risks.map((risk) => {
          const clean = { ...risk };
          delete clean.dialogs;
          return clean;
        }),
      };

      if (this.isEditMode) {
        await this.$store.dispatch("updateRiskAssessmentTemplate", payload);
      } else {
        await this.$store.dispatch("createRiskAssessmentTemplate", payload);
      }
      this.$router.back();
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>
